import { string } from "prop-types"
import { useState, useRef } from "react"
import { Pane } from "components/ui"
import { HelperShelfMessage, HelperShelfTrigger } from "components/helper_shelf"
import { Box, Spinner, StackView, Link } from "@planningcenter/tapestry-react"
import UnsplashReact, { BlobUploader, withDefaultProps } from "unsplash-react"
import Dropzone from "react-dropzone"
import { useToast, ToastProvider } from "components/ui/Toast"

const EventLogoWrapper = props => {
  return (
    <ToastProvider>
      <EventLogo {...props} />
    </ToastProvider>
  )
}

const SweetDelete = ({ url, label }) => {
  const deleteConfirm = () => {
    return alertModal(
      {
        title: `Delete ${label}?`,
        text: "This can't be undone.",
        type: "warning",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        confirmButtonText: "Yes, delete it!",
        closeOnConfirm: true
      },
      () => {
        $.ajax({
          type: "DELETE",
          headers: { "X-CSRF-Token": $.rails.csrfToken() },
          url: url
        })
      }
    )
  }
  return (
    <button className="sm-btn quiet-destroy-btn btn2 w-100% w-a@sm ml-a@sm" onClick={deleteConfirm}>
      Remove image
    </button>
  )
}

SweetDelete.propTypes = {
  url: string.isRequired,
  label: string.isRequired
}

const spinnerContainerStyles = {
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
  position: "absolute",
  zIndex: 2,
  display: "flex",
  justifyContent: "center",
  backgroundColor: "var(--t-fill-color-transparency-light-030)"
}

const accept = { "image/*": [".jpeg", ".png", ".gif"] }

const EventLogo = ({ eventName, logo, url, accessKey }) => {
  const [isEditing, setIsEditing] = useState(false)
  const [showUnsplash, setShowUnsplash] = useState(false)
  const [showSpinner, setShowSpinner] = useState(false)

  const fileInput = useRef(null)
  const dropzoneRef = useRef(null)

  const { addToast } = useToast()

  const handleToggleEditing = () => {
    setIsEditing(!isEditing)
    setShowUnsplash(!isEditing && showUnsplash)
  }

  const handleUploadClick = () => {
    fileInput.current.click()
  }

  const handleToggleUnsplash = () => setShowUnsplash(!showUnsplash)

  const handleFileInputChange = e => {
    e.preventDefault()
    handleBlobLoaded(e.target.files[0])
  }

  const handleDrop = ([file]) => handleBlobLoaded(file)

  const handleDropRejected = () => {
    addToast("Your image is too large or is not in an accepted format (JPG, PNG, or GIF).")
  }

  const handleBlobLoaded = blob => {
    setShowSpinner(true)
    const extension = blob.name ? `.${blob.name.split(".").pop()}` : ""

    const formData = new FormData()
    formData.append("event[logo]", blob, `image-${new Date().getTime()}${extension}`)

    return $.ajax({
      type: "PUT",
      headers: { "X-SRF-Token": $.rails.csrfToken() },
      data: formData,
      processData: false,
      contentType: false,
      url: url,
      success: _resp => {
        setShowSpinner(false)
      },
      error: _error => {
        setShowSpinner(false)
        alertModal({
          title: "An error occured",
          type: "error",
          text: "Your image is too large or is not in an accepted format (JPG, PNG, or GIF).",
          confirmButtonText: "Ok",
          closeOnConfirm: true
        })
      }
    })
  }

  return (
    <Pane>
      <Pane.Header flexWrap="wrap">
        <StackView axis="horizontal" gap="4px">
          <Pane.Title>Image</Pane.Title>
          <span className="p-r t-3p">
            <HelperShelfTrigger id="event_image_reqs" />
          </span>
        </StackView>
        <button className="xs-btn quiet-btn btn2" onClick={handleToggleEditing}>
          {isEditing ? "Cancel" : "Edit"}
        </button>
        <Box basis="100%">
          <HelperShelfMessage id="event_image_reqs">
            16:9 image ratio.
            <br />
            GIF, JPG, or PNG format.
            <br />
            <a
              target="_blank"
              href="https://support.planningcenteronline.com/hc/en-us/articles/18032518633115"
              rel="noreferrer"
            >
              View image specs
            </a>
          </HelperShelfMessage>
        </Box>
      </Pane.Header>
      <Pane.Body>
        {isEditing ? (
          <div>
            {showUnsplash ? (
              <div style={{ height: 325 }}>
                {/* eslint-disable-next-line jsx-a11y/no-access-key */}
                <UnsplashReact
                  accessKey={accessKey}
                  applicationName="planning_center"
                  preferredSize={{ width: 1024, height: 768 }}
                  defaultSearch={eventName}
                  Uploader={withDefaultProps(BlobUploader, {
                    onBlobLoaded: handleBlobLoaded
                  })}
                  highlightColor="#46948d"
                  photoRatio={1024 / 768}
                  columns={2}
                />
              </div>
            ) : (
              <Dropzone
                multiple={false}
                accept={accept}
                onDropAccepted={handleDrop}
                onDropRejected={handleDropRejected}
                style={{ cursor: "pointer" }}
                ref={dropzoneRef}
                noClick
                disablePreview
              >
                {({ getRootProps, getInputProps }) => (
                  <>
                    {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
                    <div
                      {...getRootProps()}
                      className="p-r dropzone"
                      onClick={e => e.preventDefault()}
                    >
                      <img src={logo} className="event-image" alt="Signup logo" />

                      <div className="dropzone--overlay">
                        <span>Drag & drop a file to upload, or</span>
                        <Link
                          style={{ marginLeft: "4px", fontWeight: "500" }}
                          to="#"
                          onClick={handleUploadClick}
                        >
                          browse
                        </Link>
                      </div>
                      {showSpinner && (
                        <div style={spinnerContainerStyles}>
                          <Spinner size="lg" />
                        </div>
                      )}
                      <input
                        {...getInputProps()}
                        ref={fileInput}
                        name="event[logo]"
                        onChange={handleFileInputChange}
                      />
                    </div>
                  </>
                )}
              </Dropzone>
            )}

            {showUnsplash ? (
              <div className="mt-2">
                <button className="sm-btn btn2 quiet-btn" onClick={handleToggleUnsplash}>
                  Cancel
                </button>
              </div>
            ) : (
              <div className="d-f@sm mt-2">
                <div className="d-f@sm mb-1 mb-0@sm mr-1@sm">
                  <button
                    className="w-100% w-a@sm mt-1 mt-0@sm sm-btn btn2 quiet-btn"
                    onClick={handleToggleUnsplash}
                  >
                    Free photos
                  </button>
                </div>
                <SweetDelete url={url} label="logo" />
              </div>
            )}
          </div>
        ) : (
          <button onClick={handleToggleEditing}>
            <img src={logo} className="event-image" alt="Signup logo" />
          </button>
        )}
      </Pane.Body>
    </Pane>
  )
}

EventLogo.propTypes = {
  eventName: string.isRequired,
  logo: string.isRequired,
  accessKey: string.isRequired,
  url: string.isRequired
}

export default EventLogoWrapper
